import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { TextField } from '@mui/material';
import { format, addDays, subDays, isValid, differenceInDays, isSameDay } from 'date-fns';
import ServiceTypeFilter from '../filters/ServiceTypeFilter';
import AreaFilter from '../filters/AreaFilter';
import { useQuery } from '@apollo/client';
import { GET_USERS, GET_PROPERTIES_WITH_RESERVATIONS, GET_SERVICE_TYPES } from '../../../graphql/queries';
import RoleBasedServiceTypeFilter from '../filters/RoleBasedServiceTypeFilter';

export default function ScheduleHeader({
  selectedDate,
  setSelectedDate,
  endDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  selectedCategories,
  handleCategoryToggle,
  selectedServiceTypes,
  onServiceTypeChange,
  availableServiceTypes,
  isRoleFilterEnabled,
  onRoleFilterToggle,
  serviceTypesLoading,
  serviceTypeFilterInfo,
  selectedAreas,
  onAreaChange,
  resetFilters,
  isAdmin,
  debugJobCategories,
  userRole,
  rightContent,
  leftContent,
  title = "Schedule",
  selectedPropertyId,
  setSelectedPropertyId
}) {
  // State for unified search
  const [inputValue, setInputValue] = useState(searchTerm);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  
  // State for filter bar expansion - default to closed on all devices
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Add state to track selected department
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  
  // Query to get all service types (without role filtering) for department filtering
  const { data: allServiceTypesData } = useQuery(GET_SERVICE_TYPES, {
    fetchPolicy: 'cache-first'
  });
  
  // Get all service types for department filtering
  const allServiceTypes = useMemo(() => {
    return allServiceTypesData?.serviceTypes?.filter(type => type.isActive) || [];
  }, [allServiceTypesData]);
  
  // Listen for window resize events to update filter bar state
  useEffect(() => {
    const handleResize = () => {
      // We no longer automatically expand filters based on screen size
      // This effect is kept in case we need to add responsive behavior in the future
    };
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [filtersExpanded]);
  
  // Sync inputValue with searchTerm when it changes externally
  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);
  
  const searchDropdownRef = useRef(null);
  
  // Get data for suggestions
  const { data: usersData } = useQuery(GET_USERS);
  
  // Get property data for search suggestions - properly memoized
  const propertyQueryVariables = useMemo(() => {
    // Only make the query if we have a valid search term
    if (!inputValue || inputValue.length < 2) {
      return null;
    }
    
    // ALIGNED WITH JOBS SEARCH: Use optimized date range for property search
    // 2 weeks back, 3 months forward (same as jobs search in useScheduleJobs)
    const now = new Date();
    const twoWeeksBack = new Date(now);
    twoWeeksBack.setDate(twoWeeksBack.getDate() - 14);
    twoWeeksBack.setHours(0, 0, 0, 0);
    
    const threeMonthsForward = new Date(now);
    threeMonthsForward.setMonth(threeMonthsForward.getMonth() + 3);
    threeMonthsForward.setHours(23, 59, 59, 999);
    
    const startDate = twoWeeksBack.toISOString().split('T')[0];
    const endDate = threeMonthsForward.toISOString().split('T')[0];
    
    // Validate that dates are valid
    if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
      console.error('Invalid date format for property query');
      return null;
    }
    
    const queryVars = {
      input: {
        limit: 20, // Reduce limit to comply with server validation
        page: 1,
        search: inputValue.trim(),
        startDate,
        endDate
      }
    };

    // Only include areas filter if specific areas are selected
    if (selectedAreas && selectedAreas.length > 0) {
      queryVars.input.areas = selectedAreas;
    }
    
    return queryVars;
  }, [inputValue, selectedAreas]); // Include selectedAreas in dependencies
  
  
  const { data: propertiesData, loading: propertiesLoading, error: propertiesError } = useQuery(GET_PROPERTIES_WITH_RESERVATIONS, {
    variables: propertyQueryVariables,
    skip: !propertyQueryVariables, // Skip query if variables are null
    fetchPolicy: 'cache-first', // Use cache first to reduce load
    errorPolicy: 'all', // Continue even if there are errors
    onError: (error) => {
      console.error('Error fetching properties for search:', error);
      // Set search suggestions to show error state instead of silent failure
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  });
  
  // Log any property query errors
  useEffect(() => {
    if (propertiesError) {
      console.error('Property query error:', propertiesError);
    }
  }, [propertiesError]);
  
  // Memoize the property data to prevent unnecessary re-renders
  const properties = useMemo(() => {
    // If query was skipped or failed, return empty array
    if (!propertyQueryVariables || propertiesError) {
      return [];
    }
    
    const props = propertiesData?.propertiesWithReservations?.properties || [];
    if (inputValue && inputValue.length >= 2) {
      

    }
    return props;
  }, [propertiesData, inputValue, propertyQueryVariables, propertiesError]);
  
  // Extract other data for suggestions  
  const users = useMemo(() => usersData?.users || [], [usersData]);
  
  // Generate suggestions based on input
  const generateSuggestions = useCallback((value) => {
    if (!value || value.length < 2) {
      return [];
    }
    
    const suggestions = [];
    const lowercaseValue = value.toLowerCase();
    
    // Map of property IDs to avoid duplicates
    const addedPropertyIds = new Set();
    
    // Add property suggestions
    if (properties && properties.length > 0) {
      
      // First try exact property name matches
      properties.forEach(property => {
        // Skip if already added
        if (addedPropertyIds.has(property.id)) return;
        
        // Check property name
        if (property.name && property.name.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            type: 'property',
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
        }
      });
      
      // Then try property code matches
      properties.forEach(property => {
        // Skip if already added
        if (addedPropertyIds.has(property.id)) return;
        
        // Check property code
        if (property.propertyCode && property.propertyCode.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            subtext: `Property Code: ${property.propertyCode}`,
            type: 'property',
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
        }
      });
      
      // Then try city matches
      properties.forEach(property => {
        // Skip if already added
        if (addedPropertyIds.has(property.id)) return;
        
        // Check property city
        if (property.city && property.city.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            subtext: `${property.city}, ${property.state}`,
            type: 'property',
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
        }
      });
      
      // Finally try address matches
      properties.forEach(property => {
        // Skip if already added
        if (addedPropertyIds.has(property.id)) return;
        
        // Check property address
        if (property.address && property.address.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            subtext: property.address,
            type: 'property',
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
        }
      });
    }
    
    // Add user suggestions
    users.forEach(user => {
      const fullName = `${user.firstName} ${user.lastName}`;
      if (fullName.toLowerCase().includes(lowercaseValue)) {
        suggestions.push({
          id: user.id,
          text: fullName,
          type: 'team',
          icon: '👤'
        });
      }
    });
    
    // Add service type suggestions using availableServiceTypes from props
    if (availableServiceTypes) {
      availableServiceTypes.forEach(serviceType => {
        if (serviceType.title.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: serviceType.id,
            text: serviceType.title,
            type: 'service',
            icon: '🔧',
            color: serviceType.color
          });
        }
      });
    }
    
    return suggestions.slice(0, 10); // Limit to top 10 suggestions
  }, [properties, users, availableServiceTypes]);
  
  // Update suggestions when input changes
  useEffect(() => {
    // Don't update suggestions if input is too short
    if (inputValue.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Use a debounce pattern to prevent too many updates
    const debounceTimeout = setTimeout(() => {
      // Only generate new suggestions if we have our data
      const newSuggestions = generateSuggestions(inputValue);
      
      setSearchSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }, 300);
    
    // Clean up the timeout
    return () => clearTimeout(debounceTimeout);
  }, [inputValue, generateSuggestions]);
  
  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle selecting a suggestion
  const handleSelectSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setInputValue(suggestion.text);
    
    if (suggestion.type === 'property') {
      // TEMPORARY: Use text search instead of property ID until backend supports propertyId
      // For property suggestions, use text search for now
      setSelectedPropertyId(null); // Don't use property ID yet
      setSearchTerm(suggestion.text);
    } else {
      // For other suggestions (team members, service types), clear property ID and use text search
      setSelectedPropertyId(null);
      setSearchTerm(suggestion.text);
    }
    
    setShowSuggestions(false);
  };
  
  // Handle search input change with debounced search term updates
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Clear selected suggestion and property ID if input changes
    if (selectedSuggestion && value !== selectedSuggestion.text) {
      setSelectedSuggestion(null);
      setSelectedPropertyId(null);
    }
    
    // Don't immediately update search term - let the debounced effect handle it
    // This prevents excessive API calls on every keystroke
  };

  // Debounced search term update to prevent excessive API calls
  useEffect(() => {
    // If input is empty, immediately clear the search and property ID
    if (inputValue === '') {
      setSearchTerm('');
      setSelectedPropertyId(null);
      return;
    }
    
    // For non-empty input, debounce the search term update
    const debounceTimeout = setTimeout(() => {
      // Update search term for all cases since we're using text search for properties too
      setSearchTerm(inputValue);
    }, 500); // 500ms debounce for search term (separate from suggestions)
    
    return () => clearTimeout(debounceTimeout);
  }, [inputValue, setSearchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    // The search term will update via the input change, causing a re-fetch
  };

  const handlePreviousDay = () => {
    // Calculate the current date range span
    const daysDifference = differenceInDays(endDate, selectedDate);
    
    // Move both dates back by one day, maintaining the range span
    const newStartDate = subDays(selectedDate, 1);
    // Ensure time is set to noon to avoid timezone issues
    newStartDate.setHours(12, 0, 0, 0);
    setSelectedDate(newStartDate);
    
    // Keep the same range span
    if (daysDifference > 0) {
      const newEndDate = subDays(endDate, 1);
      // Ensure time is set to noon to avoid timezone issues
      newEndDate.setHours(12, 0, 0, 0);
      setEndDate(newEndDate);
    } else {
      // If it's a single day, move the end date too
      setEndDate(newStartDate);
    }
  };

  const handleNextDay = () => {
    // Calculate the current date range span
    const daysDifference = differenceInDays(endDate, selectedDate);
    
    // Move both dates forward by one day, maintaining the range span
    const newStartDate = addDays(selectedDate, 1);
    // Ensure time is set to noon to avoid timezone issues
    newStartDate.setHours(12, 0, 0, 0);
    setSelectedDate(newStartDate);
    
    // Keep the same range span
    if (daysDifference > 0) {
      const newEndDate = addDays(endDate, 1);
      // Ensure time is set to noon to avoid timezone issues
      newEndDate.setHours(12, 0, 0, 0);
      setEndDate(newEndDate);
    } else {
      // If it's a single day, move the end date too
      setEndDate(newStartDate);
    }
  };
  
  const handleStartDateChange = (e) => {
    // Get the date value from the input (YYYY-MM-DD format)
    const dateStr = e.target.value;
    
    // Create a date at noon to avoid timezone issues (using parts to stay in local time)
    const [year, month, day] = dateStr.split('-').map(Number);
    const newStartDate = new Date(year, month - 1, day, 12, 0, 0);
    
    setSelectedDate(newStartDate);
    
    // If end date is before new start date, set end date equal to start date
    if (endDate < newStartDate) {
      setEndDate(newStartDate);
    }
  };
  
  const handleEndDateChange = (e) => {
    // Get the date value from the input (YYYY-MM-DD format)
    const dateStr = e.target.value;
    
    // Create a date at noon to avoid timezone issues (using parts to stay in local time)
    const [year, month, day] = dateStr.split('-').map(Number);
    const newEndDate = new Date(year, month - 1, day, 12, 0, 0);
    
    setEndDate(newEndDate);
    
    // If start date is after new end date, set start date equal to end date
    if (selectedDate > newEndDate) {
      setSelectedDate(newEndDate);
    }
  };
  
  // Function to handle user selection from dropdown
  const handleUserSelect = (userId) => {
    const user = users.find(u => u.id === userId);
    const searchValue = user ? `${user.firstName} ${user.lastName}` : '';
    setSearchTerm(searchValue);
    setSelectedPropertyId(null); // Clear property ID when selecting user
    setShowSuggestions(false);
  };
  
  // Function to handle service type selection from dropdown
  const handleServiceTypeSelect = (serviceTypeId) => {
    const serviceType = availableServiceTypes?.find(st => st.id === serviceTypeId);
    const searchValue = serviceType ? serviceType.title : '';
    setSearchTerm(searchValue);
    setSelectedPropertyId(null); // Clear property ID when selecting service type
    setShowSuggestions(false);
  };

  // Memoize department-based service type ID lists from the departments field
  const housekeepingServiceTypeIds = useMemo(() => {
    return allServiceTypes
      .filter(st => st.departments?.includes('HOUSEKEEPING'))
      .map(st => st.id);
  }, [allServiceTypes]);

  const propertyManagementServiceTypeIds = useMemo(() => {
    return allServiceTypes
      .filter(st => st.departments?.includes('PROPERTY_MANAGEMENT'))
      .map(st => st.id);
  }, [allServiceTypes]);

  // Department filter handlers using the departments field on service types
  const handleHousekeepingFilter = () => {
    if (!allServiceTypes || allServiceTypes.length === 0) return;

    if (selectedDepartment === 'housekeeping') {
      onServiceTypeChange([]);
      setSelectedDepartment(null);
      return;
    }

    onServiceTypeChange(housekeepingServiceTypeIds);
    setSelectedDepartment('housekeeping');
  };

  const handlePropertyManagementFilter = () => {
    if (!allServiceTypes || allServiceTypes.length === 0) return;

    if (selectedDepartment === 'propertyManagement') {
      onServiceTypeChange([]);
      setSelectedDepartment(null);
      return;
    }

    onServiceTypeChange(propertyManagementServiceTypeIds);
    setSelectedDepartment('propertyManagement');
  };

  // Detect which department button should be highlighted when service types change manually
  const handleServiceTypeChangeWithDepartmentCheck = (newSelectedServiceTypes) => {
    onServiceTypeChange(newSelectedServiceTypes);

    if (!allServiceTypes || allServiceTypes.length === 0) {
      setSelectedDepartment(null);
      return;
    }

    const setsMatch = (a, b) =>
      a.length === b.length && a.every(id => b.includes(id)) && b.every(id => a.includes(id));

    const matchesHousekeeping = setsMatch(newSelectedServiceTypes, housekeepingServiceTypeIds);
    const matchesPropertyManagement = setsMatch(newSelectedServiceTypes, propertyManagementServiceTypeIds);

    const uniqueAllDepartmentIds = Array.from(new Set([...housekeepingServiceTypeIds, ...propertyManagementServiceTypeIds]));
    const matchesAllDepartments = setsMatch(newSelectedServiceTypes, uniqueAllDepartmentIds);

    if (matchesAllDepartments) {
      setSelectedDepartment(null);
    } else if (matchesHousekeeping) {
      setSelectedDepartment('housekeeping');
    } else if (matchesPropertyManagement) {
      setSelectedDepartment('propertyManagement');
    } else {
      setSelectedDepartment(null);
    }
  };


  return (
    <div>
      <div className="py-4 px-2">
        <div className="flex flex-col xl:flex-row items-center md:items-start justify-between gap-4 md:gap-2">
          <div className="flex-1 min-w-0 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3">
            <h2 className="text-2xl font-bold leading-tight text-white lg:text-gray-900 sm:text-4xl sm:truncate">
              {title}
            </h2>
              

            </div>
            <p className="text-xs sm:text-sm text-white md:text-gray-500">
              {searchTerm && searchTerm.length >= 2 ? (
                <span className="inline-flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchTerm.includes(':') || searchTerm.includes('Avenue') || searchTerm.includes('Street') || searchTerm.includes('Road') ? (
                    <>
                      Searching "{searchTerm}" - showing ALL jobs for this property (2 weeks back to 2 years forward)
                    </>
                  ) : (
                    <>
                      Searching "{searchTerm}" - from 1 month ago onwards
                    </>
                  )}
                </span>
              ) : (
                <>
                  {format(selectedDate, 'MMM d, yyyy')} 
                  {!isSameDay(selectedDate, endDate) && ` to ${format(endDate, 'MMM d, yyyy')}`}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2">
              {/* Add the leftContent - only show on desktop */}
              {leftContent && (
                <div className="hidden lg:flex mr-2">
                  {leftContent}
                </div>
              )}
              
              <button
              type="button"
              onClick={handlePreviousDay}
              className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-white md:border-gray-300 rounded-md shadow-sm text-sm font-medium text-sky-950 md:text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
              Previous
              </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                // Set to noon to avoid timezone issues
                today.setHours(12, 0, 0, 0);
                setSelectedDate(today);
                setEndDate(today);
              }}
              className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-white md:border-gray-300 rounded-md shadow-sm text-sm font-medium text-sky-950 md:text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Today
            </button>
              <button
              type="button"
              onClick={handleNextDay}
              className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-white md:border-gray-300 rounded-md shadow-sm text-sm font-medium text-sky-950 md:text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
              Next
              </button>
              
              {/* Add the rightContent */}
              {rightContent}
          </div>
        </div>
      </div>

      <div className="rounded-lg shadow mt-2 sm:mt-4 mb-4">
        {/* Filter Bar Header */}
        <div 
          className="px-4 py-3 bg-white border-b border-gray-200 flex justify-between items-center cursor-pointer rounded-t-lg"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <h3 className="text-lg font-medium text-gray-700">Filters</h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
            aria-expanded={filtersExpanded}
            aria-controls="filter-section"
          >
            <span className="sr-only">{filtersExpanded ? 'Collapse filters' : 'Expand filters'}</span>
            <svg className={`h-5 w-5 transform ${filtersExpanded ? 'rotate-180' : ''} transition-transform duration-200`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {/* Filter Content */}
        {filtersExpanded && (
          <div className="px-4 py-5 pb-8 sm:px-6 bg-white rounded-b-lg" id="filter-section">
            {/* Row 1: Search, Start Date + End Date, Service Types by department */}
            <div className="space-y-4 lg:grid lg:grid-cols-3 lg:gap-4 lg:space-y-0 mb-6">
              {/* Position 1: Unified Autocomplete Search */}
              <div className="relative" ref={searchDropdownRef}>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                
                <div className="mt-1 relative">
                  <input
                    type="text"
                    id="search"
                    value={inputValue}
                    onChange={handleSearchInputChange}
                    onClick={() => {
                      // Show suggestions if there's input text
                      if (inputValue.length >= 2) {
                        setShowSuggestions(true);
                      }
                    }}
                    placeholder="Search properties or team members..."
                    className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  
                  {inputValue && (
                    <button
                      type="button"
                      onClick={() => {
                        // Clear all search-related state
                        setInputValue('');
                        setSearchTerm('');
                        setSelectedSuggestion(null);
                        setSelectedPropertyId(null);
                        setSearchSuggestions([]);
                        setShowSuggestions(false);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-600"
                      aria-label="Clear search"
                    >
                      <span className="sr-only">Clear search</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Search dropdown - Fixed mobile positioning */}
                {showSuggestions && inputValue.length >= 2 && (
                  <div 
                    className="absolute mt-1 w-full rounded-md bg-white shadow-lg border border-gray-200 max-w-sm lg:max-w-none"
                    style={{ 
                      zIndex: 50, // Reduced z-index to prevent overlap with date inputs
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      // On mobile, limit height and ensure proper positioning
                      maxHeight: window.innerWidth < 1024 ? '250px' : '300px' // Smaller on mobile
                    }}
                  >
                    <ul className="max-h-full rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {/* Loading State */}
                      {propertiesLoading && searchSuggestions.length === 0 && (
                        <li className="text-gray-500 select-none relative py-2 pl-3 pr-9 flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading properties...
                        </li>
                      )}
                      
                      {/* Error State */}
                      {propertiesError && searchSuggestions.length === 0 && (
                        <li className="text-red-500 select-none relative py-2 pl-3 pr-9">
                          Error loading properties. Please try again.
                        </li>
                      )}
                      
                      {/* No Results State */}
                      {!propertiesLoading && !propertiesError && searchSuggestions.length === 0 && (
                        <li className="text-gray-500 select-none relative py-2 pl-3 pr-9">
                          No matches found. Try a different search term.
                        </li>
                      )}
                      
                      {/* Property Suggestions */}
                      {searchSuggestions.filter(s => s.type === 'property').length > 0 && (
                        <>
                          <li className="bg-gray-50 text-gray-600 font-semibold px-3 py-1 text-xs uppercase tracking-wide">
                            Properties ({searchSuggestions.filter(s => s.type === 'property').length})
                          </li>
                          {searchSuggestions
                            .filter(s => s.type === 'property')
                            .map(suggestion => (
                              <li
                                key={`${suggestion.id}-${suggestion.type}`}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 focus:bg-blue-50"
                                onClick={() => handleSelectSuggestion(suggestion)}
                              >
                                <div className="flex items-center space-x-3">
                                  <span className="text-blue-600 text-sm">🏠</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-gray-900 font-medium truncate">
                                      {suggestion.text}
                                    </div>
                                    {suggestion.subtext && (
                                      <div className="text-gray-500 text-sm truncate">
                                        {suggestion.subtext}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                        </>
                      )}
                      
                      {/* Team Member Suggestions */}
                      {searchSuggestions.filter(s => s.type === 'team').length > 0 && (
                        <>
                          <li className="bg-gray-50 text-gray-600 font-semibold px-3 py-1 text-xs uppercase tracking-wide">
                            Team Members ({searchSuggestions.filter(s => s.type === 'team').length})
                          </li>
                          {searchSuggestions
                            .filter(s => s.type === 'team')
                            .map(suggestion => (
                              <li
                                key={`${suggestion.id}-${suggestion.type}`}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-green-50 focus:bg-green-50"
                                onClick={() => handleSelectSuggestion(suggestion)}
                              >
                                <div className="flex items-center space-x-3">
                                  <span className="text-green-600 text-sm">👤</span>
                                  <div className="text-gray-900 font-medium truncate">
                                    {suggestion.text}
                                  </div>
                                </div>
                              </li>
                            ))}
                        </>
                      )}
                      
                      {/* Service Type Suggestions */}
                      {searchSuggestions.filter(s => s.type === 'service').length > 0 && (
                        <>
                          <li className="bg-gray-50 text-gray-600 font-semibold px-3 py-1 text-xs uppercase tracking-wide">
                            Service Types ({searchSuggestions.filter(s => s.type === 'service').length})
                          </li>
                          {searchSuggestions
                            .filter(s => s.type === 'service')
                            .map(suggestion => (
                              <li
                                key={`${suggestion.id}-${suggestion.type}`}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-purple-50 focus:bg-purple-50"
                                onClick={() => handleSelectSuggestion(suggestion)}
                              >
                                <div className="flex items-center space-x-3">
                                  <span 
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: suggestion.color || '#6B7280' }}
                                  ></span>
                                  <div className="text-gray-900 font-medium truncate">
                                    {suggestion.text}
                                  </div>
                                </div>
                              </li>
                            ))}
                        </>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Position 2: Date Range Selection - Combined Start Date + End Date */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Date Range</label>
                <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-3">
                  <div>
                    <label htmlFor="startDate" className="block mb-1 text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      value={format(selectedDate, 'yyyy-MM-dd')}
                      onChange={handleStartDateChange}
                      onClick={() => setShowSuggestions(false)} // Close search dropdown when clicking date input
                      className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="endDate" className="block mb-1 text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      id="endDate"
                      value={format(endDate, 'yyyy-MM-dd')}
                      onChange={handleEndDateChange}
                      onClick={() => setShowSuggestions(false)} // Close search dropdown when clicking date input
                      className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Range: {format(selectedDate, 'MMM d')} to {format(endDate, 'MMM d, yyyy')}
                </div>
              </div>

              {/* Position 3: Service Types by Department */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Service Types by Department</label>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={handleHousekeepingFilter}
                    className={`w-full px-3 py-2 text-sm font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      selectedDepartment === 'housekeeping'
                        ? 'bg-green-50 text-green-700 border-green-200 focus:ring-green-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Housekeeping</span>
                      {selectedDepartment === 'housekeeping' && (
                        <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={handlePropertyManagementFilter}
                    className={`w-full px-3 py-2 text-sm font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      selectedDepartment === 'propertyManagement'
                        ? 'bg-blue-50 text-blue-700 border-blue-200 focus:ring-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Property Management</span>
                      {selectedDepartment === 'propertyManagement' && (
                        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Service Types, Areas */}
            <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
              {/* Position 1: Service Type Filter */}
              <div>
                <RoleBasedServiceTypeFilter
                  selectedServiceTypes={selectedServiceTypes}
                  onChange={handleServiceTypeChangeWithDepartmentCheck}
                  availableServiceTypes={availableServiceTypes}
                  loading={serviceTypesLoading}
                />
              </div>
              
              {/* Position 2: Area Filter */}
              <div>
                <AreaFilter
                  selectedAreas={selectedAreas}
                  onChange={onAreaChange}
                />
              </div>
            </div>

            {/* Reset Filters Button */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  // Also clear property-specific state
                  setSelectedPropertyId(null);
                  setSelectedSuggestion(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 